import {
  type PaginationState,
  type SortingState,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import { RecentOutputItem } from '@eluve/api-contract';
import {
  Button,
  ColDefBuilder,
  Icon,
  Link,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  ServerDataTable,
  Stack,
  Text,
  useToast,
} from '@eluve/components';
import { LlmOutputTypesLookup } from '@eluve/graphql-types';

import { getLangfuseSessionUrlForAppointment } from '../tenant/$tenantId/dash/utils/get-langfuse-url';

const formatDuration = (seconds: number | null): string => {
  if (seconds === null) return 'N/A';
  const rounded = Math.round(seconds);
  if (rounded < 60) return `${rounded}s`;
  const minutes = Math.floor(rounded / 60);
  const remainingSeconds = rounded % 60;
  if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
};

const getUserDisplayName = (row: RecentOutputItem) =>
  [row.firstName, row.lastName].filter(Boolean).join(' ') ||
  row.email ||
  'Unknown';

const ALL_FILTER = 'ALL';

const createColumns = (
  langfuseBaseUrl?: string | null,
  langfuseProjectId?: string | null,
) =>
  new ColDefBuilder<RecentOutputItem>()
    .defaultSortable('tenantName', 'Organization')
    .linkSortable(
      'appointmentName',
      (row) =>
        `/admin/organizations/${row.tenantId}/appointments/${row.appointmentId}/details`,
      'Appointment',
    )
    .colDef({
      id: 'user',
      header: 'User',
      accessorFn: getUserDisplayName,
      cell: ({ row }) => {
        const name = getUserDisplayName(row.original);
        if (row.original.userId) {
          return <Link to={`/admin/users/${row.original.userId}`}>{name}</Link>;
        }
        return <Text size="xs">{name}</Text>;
      },
      enableSorting: true,
    })
    .defaultSortable('status', 'Status')
    .defaultSortable('source', 'Source')
    .colDef({
      id: 'promptTemplate',
      header: 'Prompt Template',
      accessorKey: 'promptTemplateName',
      cell: ({ row }) => {
        const name = row.original.promptTemplateName;
        return (
          <Text size="xs" color={name ? 'primary' : 'tertiary'}>
            {name ?? 'N/A'}
          </Text>
        );
      },
      enableSorting: true,
    })
    .defaultSortable('outputType', 'Output Type')
    .colDef({
      id: 'outputTemplate',
      header: 'Output Template',
      accessorKey: 'outputTemplateName',
      cell: ({ row }) => {
        const name = row.original.outputTemplateName;
        return (
          <Text size="xs" color={name ? 'primary' : 'tertiary'}>
            {name ?? 'N/A'}
          </Text>
        );
      },
      enableSorting: true,
    })
    .colDef({
      id: 'model',
      header: 'Model',
      accessorFn: (row) =>
        [row.modelArgsName, row.modelType].filter(Boolean).join(' / ') || 'N/A',
      cell: ({ row }) => {
        const display =
          [row.original.modelArgsName, row.original.modelType]
            .filter(Boolean)
            .join(' / ') || 'N/A';
        return (
          <Text
            size="xs"
            color={row.original.modelArgsName ? 'primary' : 'tertiary'}
          >
            {display}
          </Text>
        );
      },
      enableSorting: true,
    })
    .colDef({
      id: 'duration',
      header: 'Duration',
      accessorKey: 'durationSeconds',
      cell: ({ row }) => (
        <Text size="xs">{formatDuration(row.original.durationSeconds)}</Text>
      ),
      enableSorting: true,
    })
    .dateSortable('createdAt', 'Created At')
    .dateSortable('completedAt', 'Completed At')
    .colDef({
      id: 'langfuse',
      header: 'Langfuse',
      cell: ({ row }) => {
        const url = getLangfuseSessionUrlForAppointment(
          row.original.appointmentId,
          langfuseBaseUrl,
          langfuseProjectId,
        );
        if (!url) return null;
        return (
          <Link to={url} target="_blank" rel="noopener noreferrer">
            <Icon name="ExternalLink" />
          </Link>
        );
      },
      enableSorting: false,
    })
    .build();

interface RecentOutputsTabProps {
  langfuseBaseUrl?: string | null;
  langfuseProjectId?: string | null;
}

export const RecentOutputsTab: React.FC<RecentOutputsTabProps> = ({
  langfuseBaseUrl,
  langfuseProjectId,
}) => {
  const api = useApiClient();
  const [data, setData] = useState<RecentOutputItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [fetchDurationMs, setFetchDurationMs] = useState<number | null>(null);
  const toast = useToast();
  const [outputTypeFilter, setOutputTypeFilter] = useState(ALL_FILTER);
  const [statusFilter, setStatusFilter] = useState(ALL_FILTER);
  const [maxAgeDays, setMaxAgeDays] = useState('1');
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'createdAt', desc: true },
  ]);

  const columns = useMemo(
    () => createColumns(langfuseBaseUrl, langfuseProjectId),
    [langfuseBaseUrl, langfuseProjectId],
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    const startTime = performance.now();
    const sort = sorting[0];
    const result = await api.adminOnCall.getRecentOutputs({
      query: {
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        sortBy: sort?.id,
        sortDirection: sort?.desc ? 'desc' : 'asc',
        outputType:
          outputTypeFilter === ALL_FILTER ? undefined : outputTypeFilter,
        status: statusFilter === ALL_FILTER ? undefined : statusFilter,
        maxAgeDays: Number(maxAgeDays),
      },
    });
    const elapsed = Math.round(performance.now() - startTime);
    setFetchDurationMs(elapsed);
    if (result.status === 200) {
      setData(result.body.items);
      setTotalCount(result.body.totalCount);
    } else {
      toast.error('Failed to load recent outputs. Please try again.');
    }
    setLoading(false);
  }, [
    api,
    outputTypeFilter,
    statusFilter,
    maxAgeDays,
    pagination.pageIndex,
    pagination.pageSize,
    sorting,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const outputTypes = useMemo(
    () => Object.values(LlmOutputTypesLookup).sort(),
    [],
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    pageCount: Math.ceil(totalCount / pagination.pageSize),
    state: { pagination, sorting },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
  });

  return (
    <Stack gap={2}>
      <Stack gap={2} direction="horizontal">
        <Select value={outputTypeFilter} onValueChange={setOutputTypeFilter}>
          <SelectTrigger className="w-[360px]">
            <SelectValue placeholder="Filter by output type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_FILTER}>All Output Types</SelectItem>
            {outputTypes.map((type) => (
              <SelectItem key={type} value={type}>
                {type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_FILTER}>All Statuses</SelectItem>
            <SelectItem value="COMPLETED">COMPLETED</SelectItem>
            <SelectItem value="FAILED">FAILED</SelectItem>
          </SelectContent>
        </Select>
        <Select value={maxAgeDays} onValueChange={setMaxAgeDays}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Date range" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="1">Last 24 hours</SelectItem>
            <SelectItem value="2">Last 2 days</SelectItem>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="14">Last 14 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
          </SelectContent>
        </Select>
        <Stack
          className="ml-auto"
          fullWidth={false}
          gap={2}
          direction="horizontal"
        >
          <Text inline size="xs" color="tertiary">
            {loading
              ? 'Loading...'
              : `${totalCount} output${totalCount !== 1 ? 's' : ''}${fetchDurationMs !== null ? ` · ${fetchDurationMs >= 1000 ? `${(fetchDurationMs / 1000).toFixed(1)}s` : `${fetchDurationMs}ms`}` : ''}`}
          </Text>
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            <Icon name="RefreshCw" size="auto" />
            Refresh
          </Button>
        </Stack>
      </Stack>
      <Stack className="rounded-md border">
        <ServerDataTable<RecentOutputItem>
          table={table}
          isPending={loading}
          placeholder="No recent outputs found."
        />
      </Stack>
    </Stack>
  );
};
